/**
 * Audiences API Client
 * Story: E007-S027 Audience Manager & Targeting Optimization
 */

import { apiClient } from './apiClient';
import type {
  Audience,
  AudienceListResponse,
  AudiencePerformanceDetail,
  AudienceFilters,
  CreateAudienceRequest,
  CreateAudienceResponse,
  CreateLookalikeRequest,
  CreateLookalikeResponse,
  UpdateAudienceRequest,
  SyncResponse,
  Recommendation,
  Platform,
  AudienceCriteria,
  LocationSuggestion,
  PlatformInfo,
} from '../types/audiences';

const BASE_URL = '/api/v1/audiences';

/**
 * List audiences with optional filtering, sorting, and pagination
 */
export async function listAudiences(
  filters: AudienceFilters = {}
): Promise<AudienceListResponse> {
  const params = new URLSearchParams();
  
  if (filters.search) params.append('search', filters.search);
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.type) params.append('audience_type', filters.type);
  if (filters.status) params.append('status', filters.status);
  if (filters.sort) params.append('sort', filters.sort);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  
  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
  
  const response = await apiClient.get(url);
  return response.data;
}

/**
 * Get a single audience by ID
 */
export async function getAudience(audienceId: string): Promise<Audience> {
  const response = await apiClient.get(`${BASE_URL}/${audienceId}`);
  return response.data;
}

/**
 * Create a new audience
 */
export async function createAudience(
  request: CreateAudienceRequest
): Promise<CreateAudienceResponse> {
  const response = await apiClient.post(BASE_URL, request);
  return response.data;
}

/**
 * Update an audience
 */
export async function updateAudience(
  audienceId: string,
  request: UpdateAudienceRequest
): Promise<Audience> {
  const response = await apiClient.put(`${BASE_URL}/${audienceId}`, request);
  return response.data;
}

/**
 * Delete (archive) an audience
 */
export async function deleteAudience(audienceId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${audienceId}`);
}

/**
 * Create a lookalike audience from a seed audience
 */
export async function createLookalike(
  seedAudienceId: string,
  request: CreateLookalikeRequest
): Promise<CreateLookalikeResponse> {
  const response = await apiClient.post(
    `${BASE_URL}/${seedAudienceId}/lookalike`,
    request
  );
  return response.data;
}

/**
 * Get detailed performance metrics for an audience
 */
export async function getAudiencePerformance(
  audienceId: string,
  startDate?: string,
  endDate?: string
): Promise<AudiencePerformanceDetail> {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const queryString = params.toString();
  const url = queryString
    ? `${BASE_URL}/${audienceId}/performance?${queryString}`
    : `${BASE_URL}/${audienceId}/performance`;
  
  const response = await apiClient.get(url);
  return response.data;
}

/**
 * Get targeting recommendations for an audience
 */
export async function getAudienceRecommendations(
  audienceId: string
): Promise<Recommendation[]> {
  const response = await apiClient.get(`${BASE_URL}/${audienceId}/recommendations`);
  return response.data;
}

/**
 * Sync audience to another platform
 */
export async function syncToPlatform(
  audienceId: string,
  targetPlatform: Platform
): Promise<SyncResponse> {
  const response = await apiClient.post(`${BASE_URL}/${audienceId}/sync`, {
    target_platform: targetPlatform,
  });
  return response.data;
}

/**
 * Get list of supported platforms
 */
export async function getPlatforms(): Promise<{ platforms: PlatformInfo[] }> {
  const response = await apiClient.get(`${BASE_URL}/platforms/list`);
  return response.data;
}

/**
 * Get interest suggestions for targeting
 */
export async function suggestInterests(
  query: string,
  platform: Platform = 'meta'
): Promise<{ suggestions: string[]; platform: Platform }> {
  const params = new URLSearchParams({ query, platform });
  const response = await apiClient.get(`${BASE_URL}/interests/suggest?${params}`);
  return response.data;
}

/**
 * Get location suggestions for targeting
 */
export async function suggestLocations(
  query: string
): Promise<{ suggestions: LocationSuggestion[] }> {
  const params = new URLSearchParams({ query });
  const response = await apiClient.get(`${BASE_URL}/locations/suggest?${params}`);
  return response.data;
}

/**
 * Estimate audience size based on criteria
 */
export async function estimateAudienceSize(
  platform: Platform,
  criteria?: AudienceCriteria
): Promise<{ estimated_size: number; platform: Platform; criteria: AudienceCriteria }> {
  const params = new URLSearchParams({ platform });
  const response = await apiClient.post(
    `${BASE_URL}/estimate-size?${params}`,
    criteria || {}
  );
  return response.data;
}

// ============================================
// React Query Hooks (optional, for convenience)
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export const AUDIENCE_QUERY_KEYS = {
  all: ['audiences'] as const,
  list: (filters: AudienceFilters) => [...AUDIENCE_QUERY_KEYS.all, 'list', filters] as const,
  detail: (id: string) => [...AUDIENCE_QUERY_KEYS.all, 'detail', id] as const,
  performance: (id: string, startDate?: string, endDate?: string) =>
    [...AUDIENCE_QUERY_KEYS.all, 'performance', id, startDate, endDate] as const,
  recommendations: (id: string) => [...AUDIENCE_QUERY_KEYS.all, 'recommendations', id] as const,
};

/**
 * Hook to list audiences
 */
export function useAudiences(filters: AudienceFilters = {}) {
  return useQuery({
    queryKey: AUDIENCE_QUERY_KEYS.list(filters),
    queryFn: () => listAudiences(filters),
  });
}

/**
 * Hook to get a single audience
 */
export function useAudience(audienceId: string) {
  return useQuery({
    queryKey: AUDIENCE_QUERY_KEYS.detail(audienceId),
    queryFn: () => getAudience(audienceId),
    enabled: !!audienceId,
  });
}

/**
 * Hook to get audience performance
 */
export function useAudiencePerformance(
  audienceId: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: AUDIENCE_QUERY_KEYS.performance(audienceId, startDate, endDate),
    queryFn: () => getAudiencePerformance(audienceId, startDate, endDate),
    enabled: !!audienceId,
  });
}

/**
 * Hook to get audience recommendations
 */
export function useAudienceRecommendations(audienceId: string) {
  return useQuery({
    queryKey: AUDIENCE_QUERY_KEYS.recommendations(audienceId),
    queryFn: () => getAudienceRecommendations(audienceId),
    enabled: !!audienceId,
  });
}

/**
 * Hook to create an audience
 */
export function useCreateAudience() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: CreateAudienceRequest) => createAudience(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUDIENCE_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to update an audience
 */
export function useUpdateAudience() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ audienceId, request }: { audienceId: string; request: UpdateAudienceRequest }) =>
      updateAudience(audienceId, request),
    onSuccess: (_, { audienceId }) => {
      queryClient.invalidateQueries({ queryKey: AUDIENCE_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: AUDIENCE_QUERY_KEYS.detail(audienceId) });
    },
  });
}

/**
 * Hook to delete an audience
 */
export function useDeleteAudience() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (audienceId: string) => deleteAudience(audienceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUDIENCE_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to create a lookalike audience
 */
export function useCreateLookalike() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ seedAudienceId, request }: { seedAudienceId: string; request: CreateLookalikeRequest }) =>
      createLookalike(seedAudienceId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUDIENCE_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to sync audience to another platform
 */
export function useSyncToPlatform() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ audienceId, targetPlatform }: { audienceId: string; targetPlatform: Platform }) =>
      syncToPlatform(audienceId, targetPlatform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUDIENCE_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to estimate audience size
 */
export function useEstimateAudienceSize() {
  return useMutation({
    mutationFn: ({ platform, criteria }: { platform: Platform; criteria?: AudienceCriteria }) =>
      estimateAudienceSize(platform, criteria),
  });
}
